import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';

import { handleGTMHomepageBannerEvent } from '../../helpers/googleTag';
import { useOnScreen } from '../../hooks/useOnScreen';
import { useRuntimeConfig } from '../../hooks/useRuntimeConfig';
import styles from './MyHavenExtrasBanner.module.scss';

const Logo = (): ReactElement => (
  <img
    src="/assets/my-haven-extras-logo.svg"
    alt="My Haven Extras"
    className={styles.logo}
  />
);

const RewardsUnavailableBanner = (): ReactElement => (
  <div className={styles.banner}>
    <Logo />
    <div className={styles.message}>
      <p>
        Reward Gateway is temporarily unavailable while the provider is carrying
        out checks on its platform. As a result, access to discounts and rewards
        via the app and website is currently unavailable.
      </p>
      <p>We apologise for the inconvenience and thank you for your patience.</p>
    </div>
  </div>
);

const RewardsBanner = ({
  rewardsUrl,
}: {
  rewardsUrl: string;
}): ReactElement => {
  const { PUBLIC_APP_URL } = useRuntimeConfig();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref);
  const [isViewed, setIsViewed] = useState(false);

  const gtmOptions = useMemo(
    () => ({
      category: 'homepage banner',
      type: 'owners',
      variant: 'homepage my extras rewards',
      title: 'my haven extras',
      promotion_message: 'Rewards and discounts for high street brands',
      image_url: `${PUBLIC_APP_URL}/assets/my-haven-extras-logo.svg`,
      link_url: rewardsUrl,
      page_position: '2/1',
    }),
    [PUBLIC_APP_URL, rewardsUrl]
  );

  useEffect(() => {
    if (isVisible) setIsViewed(true);
  }, [isVisible]);

  useEffect(() => {
    if (isViewed) {
      handleGTMHomepageBannerEvent('banner impression', gtmOptions);
    }
  }, [isViewed, gtmOptions]);

  const handleClick = () => {
    handleGTMHomepageBannerEvent('banner click', gtmOptions);
    window.open(rewardsUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.banner} ref={ref}>
      <Logo />
      <p className={styles.text}>
        <strong>Rewards and discounts</strong> for high street brands
      </p>
      <Button variant="outlined" className={styles.cta} onClick={handleClick}>
        See rewards
      </Button>
    </div>
  );
};

// The gateway URL is blanked in the manifests while Reward Gateway is down.
const MyHavenExtrasBanner = (): ReactElement => {
  const { PUBLIC_OWNER_REWARDS_GATEWAY_URL } = useRuntimeConfig();

  if (!PUBLIC_OWNER_REWARDS_GATEWAY_URL) return <RewardsUnavailableBanner />;

  return <RewardsBanner rewardsUrl={PUBLIC_OWNER_REWARDS_GATEWAY_URL} />;
};

export default MyHavenExtrasBanner;
